import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useManagerModal } from "../contexts/ManagerModalContext";
import { fetchManagers, User } from "../Services/APIService";
import { impersonateUser } from "../Services/APIService";

const ManagerModal: React.FC = () => {
  const { isOpen, closeManagerModal } = useManagerModal();
  const navigate = useNavigate();

  const [managers, setManagers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (!isOpen) return;

    const loadManagers = async () => {
      setLoading(true);
      const data = await fetchManagers();
      setManagers(data);
      setLoading(false);
    };

    loadManagers();
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSelect = async (manager: User) => {
    const result = await impersonateUser(manager.userId);

    if (!result?.user) {
      alert("Impersonation failed");
      return;
    }

    if (!localStorage.getItem("OriginalUserId")) {
      localStorage.setItem("OriginalUserId", localStorage.getItem("UserId") || "");
      localStorage.setItem("OriginalToken", localStorage.getItem("authToken") || "");
      localStorage.setItem("OriginalUserTypeId", localStorage.getItem("userTypeId") || "");
      localStorage.setItem("OriginalUserFullName", localStorage.getItem("userFullName") || "");
    }

    localStorage.setItem("authToken", result.accessToken);
    localStorage.setItem("refreshToken", result.refreshToken);
    localStorage.setItem("UserId", String(result.user.userId));
    localStorage.setItem("userTypeId", String(result.user.userTypeId));
    localStorage.setItem("tenantId", String(result.user.tenantId));
    localStorage.setItem("groupId", String(result.user.groupId));
    localStorage.setItem("userFullName", String(result.user.userName));

    localStorage.setItem("IsImpersonating", "true");

    window.location.replace(`/#/dashboard`);
    window.location.reload();
  };

  const filteredManagers = managers.filter(
    (manager) =>
      manager.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      manager.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <style>{`
        .mgr-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0, 0, 0, 0.45);
          backdrop-filter: blur(2px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          animation: mgrFadeIn 0.2s ease-out;
        }

        @keyframes mgrFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes mgrSlideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }

        @keyframes mgrSpin {
          to { transform: rotate(360deg); }
        }

        .mgr-modal {
          background: #fff;
          border-radius: 16px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
          width: 420px;
          max-width: 95vw;
          max-height: 85vh;
          overflow: hidden;
          animation: mgrSlideUp 0.3s ease-out;
          position: relative;
          display: flex;
          flex-direction: column;
        }

        /* ✅ Gradient header — exactly like UserModal */
        .mgr-header {
          background: linear-gradient(45deg, #84556bb3 34%, #000080b3);
          color: white;
          padding: 22px 20px 20px 20px;
          position: relative;
          overflow: hidden;
          flex-shrink: 0;
        }

        .mgr-header-title {
          font-size: 22px;
          font-weight: 700;
          margin: 0 0 4px 0;
          position: relative;
          z-index: 1;
        }

        .mgr-header-subtitle {
          font-size: 13px;
          opacity: 0.85;
          margin: 0;
          position: relative;
          z-index: 1;
        }

        /* ✅ Close button — exactly like UserModal */
        .mgr-close-btn {
          position: absolute;
          top: 14px;
          right: 14px;
          background: rgba(255, 255, 255, 0.2);
          border: none;
          color: white;
          width: 30px;
          height: 30px;
          border-radius: 50%;
          font-size: 18px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          z-index: 2;
          line-height: 1;
        }

        .mgr-close-btn:hover {
          background: rgba(255, 255, 255, 0.35);
          transform: rotate(90deg);
        }

        /* Search */
        .mgr-search-wrap {
          padding: 16px 16px 8px 16px;
        }

        .mgr-search-inner {
          position: relative;
        }

        .mgr-search-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 14px;
          color: #9ca3af;
        }

        .mgr-search-input {
          width: 100%;
          padding: 10px 12px 10px 36px;
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          font-size: 14px;
          outline: none;
          background: #f9fafb;
          box-sizing: border-box;
          color: #111827;
          transition: border-color 0.2s;
        }

        .mgr-search-input:focus {
          border-color: #7c3aed;
          box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1);
        }

        /* List */
        .mgr-body {
          overflow-y: auto;
          flex: 1;
          padding-bottom: 8px;
        }

        .mgr-body::-webkit-scrollbar { width: 8px; }
        .mgr-body::-webkit-scrollbar-track { background: #f3f4f6; border-radius: 4px; }
        .mgr-body::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 4px; }
        .mgr-body::-webkit-scrollbar-thumb:hover { background: #9ca3af; }

        .mgr-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        /* ✅ List item — exactly like UserModal rows */
        .mgr-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          cursor: pointer;
          transition: background 0.15s;
          border-bottom: 1px solid #f3f4f6;
        }

        .mgr-item:hover {
          background: #f5f3ff;
        }

        /* ✅ Avatar — rounded square like UserModal */
        .mgr-avatar {
          width: 42px;
          height: 42px;
          border-radius: 10px;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 700;
          font-size: 17px;
          flex-shrink: 0;
        }

        .mgr-info {
          flex: 1;
          min-width: 0;
        }

        .mgr-name {
          font-size: 14px;
          font-weight: 600;
          color: #111827;
          margin: 0 0 2px 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .mgr-email {
          font-size: 12px;
          color: #6b7280;
          margin: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .mgr-arrow {
          color: #9ca3af;
          font-size: 16px;
          flex-shrink: 0;
        }

        /* Loading */
        .mgr-loading {
          text-align: center;
          padding: 48px 24px;
        }

        .mgr-spinner {
          width: 44px;
          height: 44px;
          border: 4px solid #e5e7eb;
          border-top-color: #7c3aed;
          border-radius: 50%;
          animation: mgrSpin 0.8s linear infinite;
          margin: 0 auto 14px;
        }

        .mgr-loading-text {
          color: #6b7280;
          font-size: 14px;
          margin: 0;
        }

        /* Empty */
        .mgr-empty {
          text-align: center;
          padding: 48px 24px;
        }

        .mgr-empty-icon {
          font-size: 36px;
          margin-bottom: 8px;
        }

        .mgr-empty-text {
          color: #9ca3af;
          font-size: 14px;
          font-weight: 500;
          margin: 0;
        }

        /* Footer */
        .mgr-footer {
          padding: 12px 16px 16px 16px;
          border-top: 1px solid #f3f4f6;
          flex-shrink: 0;
        }

        /* ✅ Cancel button — exactly like UserModal */
        .mgr-cancel-btn {
          width: 100%;
          padding: 11px;
          border-radius: 10px;
          border: 1px solid #e5e7eb;
          background: #f9fafb;
          color: #374151;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .mgr-cancel-btn:hover {
          background: #f3f4f6;
          border-color: #d1d5db;
        }
      `}</style>

      <div className="mgr-overlay" onClick={closeManagerModal}>
        <div className="mgr-modal" onClick={(e) => e.stopPropagation()}>

          {/* ✅ Gradient Header */}
          <div className="mgr-header">
            <button className="mgr-close-btn" onClick={closeManagerModal} aria-label="Close">
              ×
            </button>
            <h2 className="mgr-header-title">Login as User</h2>
            <p className="mgr-header-subtitle">Select a User account to continue</p>
          </div>

          {/* Search */}
          <div className="mgr-search-wrap">
            <div className="mgr-search-inner">
              <span className="mgr-search-icon">🔍</span>
              <input
                type="text"
                className="mgr-search-input"
                placeholder="Search managers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Body */}
          <div className="mgr-body">
            {loading ? (
              <div className="mgr-loading">
                <div className="mgr-spinner" />
                <p className="mgr-loading-text">Loading Users...</p>
              </div>
            ) : filteredManagers.length === 0 ? (
              <div className="mgr-empty">
                <div className="mgr-empty-icon">👤</div>
                <p className="mgr-empty-text">
                  {searchTerm ? "No User match your search" : "No User found"}
                </p>
              </div>
            ) : (
              <ul className="mgr-list">
                {filteredManagers.map((manager) => (
                  <li
                    key={manager.userId}
                    className="mgr-item"
                    onClick={() => handleSelect(manager)}
                  >
                    <div className="mgr-avatar">
                      {manager.userName.charAt(0).toUpperCase()}
                    </div>
                    <div className="mgr-info">
                      <p className="mgr-name">
                        {manager.userName}({manager.userTypeName})
                      </p>
                      <p className="mgr-email">{manager.email}</p>
                    </div>
                    <span className="mgr-arrow">→</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Footer */}
          <div className="mgr-footer">
            <button className="mgr-cancel-btn" onClick={closeManagerModal}>
              Cancel
            </button>
          </div>

        </div>
      </div>
    </>
  );
};

export default ManagerModal;